import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useOrderContext } from "Hooks/useOrderContext";
import _ from "lodash";
import { useTranslation } from "react-i18next";

import ActionButtons from "../../../../../../components/Generic/ActionButtons";
import ActualInventory from "../../../../../../components/Generic/ActualInventory";
import QuotedInventory from "../../../../../../components/Generic/QuotedInventory";
import { NotificationToast } from "../../../../../../components/Generic/Toast/NotificationToast";
import LoadingModal from "../../../../../../components/LoadingModal";
import { ExternalList } from "../../../../../../context/MovingResources";
import { useAppState } from "../../../../../../context/StateContext";
import { Destination } from "../../../../../../models/destination";
import { Job } from "../../../../../../models/job";
import { JobMode } from "../../../../../../models/job.mode";
import { JobType } from "../../../../../../models/jobType";
import { DifferentLocation as LocationAddress } from "../../../../../../models/location";
import { Order } from "../../../../../../models/order";
import { ItemJob, QuotedItem, RoomJob } from "../../../../../../models/sales";
import { StorageBox, StorageBoxAssociateJob } from "../../../../../../models/storage.box";
import { StorageBoxType } from "../../../../../../models/storage.box.type";
import DestinationService from "../../../../../../services/destination.service";
import JobTypeService from "../../../../../../services/job.type.service";
import ModeJobService from "../../../../../../services/mode.job.service";
import StorageBoxTypeService from "../../../../../../services/storage.box.type.service";
import WarehousingService from "../../../../../../services/warehousing.service";
import {
  DESTINATION,
  ItemCharacteristicsFilter,
  ItemJobSubStatus,
  Status,
} from "../../../../../../util/enums";
import { DestinationKeys, ModeJobKeys } from "../../../../../../util/queries";
import Header from "./components/Header";
import Cards from "./components/Header/components/Cards";
import StorageBoxModal, { StorageBoxModalHandle } from "./components/StorageBoxModal";
import useStyles from "./styles";
import ItemJobService from "Services/item.job.service";
import { JobInventoryPDF } from "Models/job.inventory.pdf";
import { StorageBoxLoadedTypeEnum } from "Util/enums/storageBox";

enum InventoryType {
  ACTUAL = "Actual",
  QUOTED = "Quoted",
}

interface OrderInventoryProps {
  order?: Order;
  inventory: RoomJob[];
  actualInventory: ItemJob[];
  quoteJobId: number;
  destinations: LocationAddress[];
  defaultMode: string;
  orderStatus?: string;
  quotedViewOnly?: boolean;
  isOnViewMode?: boolean;
  externalList?: ExternalList;
  storageBoxes?: StorageBox[];
  isUpdating?: boolean;
  callbackFunction?: () => void;
  update?: (attribute: string, value: RoomJob[] | ItemJob[] | Order | StorageBox[] | JobInventoryPDF | null) => void;
  updateDestinationAddress?: (value: LocationAddress[]) => void;
  addStorageBox?: () => void;
  removeStorageBox?: () => void;
  onSave?: () => void;
  onSavePublish?: () => void;
  onCancel?: () => void;
  showActionButtons?: boolean;
  compact?: boolean;
}

const OrderInventory = ({
  order,
  inventory,
  actualInventory = [],
  quoteJobId,
  destinations,
  defaultMode,
  orderStatus = "",
  isOnViewMode = false,
  quotedViewOnly = false,
  onSave,
  onSavePublish,
  onCancel,
  showActionButtons = true,
  compact = false,
  isUpdating,
  update,
  addStorageBox,
  removeStorageBox,
  externalList,
  storageBoxes = [],
  callbackFunction,
}: OrderInventoryProps) => {
  const { t: translate } = useTranslation();
  const { currentUser } = useAppState();
  const classes = useStyles();
  const [typeOfInventory, setTypeOfInventory] = useState<string>(InventoryType.ACTUAL);
  const [customDestinations, setCustomDestinations] = useState<Destination[]>(
    externalList?.destinationList || []
  );
  const [modes, setModes] = useState<JobMode[]>(externalList?.jobModeList || []);

  const { handleUpdateOrder } = useOrderContext();

  const queryClient = useQueryClient();

  const [actualInventoryFilters, setActualInventoryFilters] = useState<any>();

  const isCreatingNewOrder = useMemo(() => {
    return !order?.id || quoteJobId < 0;
  }, [order?.id, quoteJobId]);

  const { isFetching: isActualInventoryLoading } = useQuery({
    queryKey: ["actual.inventory", order?.id, actualInventoryFilters],
    queryFn: async () => {
      const params = {
        jobId: order?.id,
        companyId: currentUser?.companyId,
        subStatus: ItemJobSubStatus.ACTUAL,
        ...(actualInventoryFilters || {}),
      };

      const response = await ItemJobService.getInventory(params);

      if (!!actualInventory?.length) handleUpdateOrder?.("actualInventory", actualInventory);
      else handleUpdateOrder?.("actualInventory", response?.data?.actualInventory || []);

      return response?.data?.actualInventory || actualInventory;
    },
    enabled: !!handleUpdateOrder && !isCreatingNewOrder && !!order?.id && !actualInventory?.length,
    refetchOnWindowFocus: false,
  });

  const handleActualInventoryUpdate = useCallback(
    (itemsJobs: ItemJob[]) => {
      if (update) {
        update("actualInventory", itemsJobs);
      }

      if (!isCreatingNewOrder && order?.id) {
        console.log("Should invalidate actual inventory.");
        // queryClient.invalidateQueries({
        //   queryKey: ["actual.inventory", order?.id],
        // });
      }
    },
    [update, queryClient, order?.id, isCreatingNewOrder]
  );

  const applyLocalFilters = useCallback((items: ItemJob[], filters: any): ItemJob[] => {
    if (!filters) return items;

    return items.filter((item) => {
      if (filters.statuses?.length && !filters.statuses.includes(item.status)) {
        return false;
      }

      if (filters.roomIds?.length && item.roomId && !filters.roomIds.includes(item.roomId)) {
        return false;
      }

      if (
        filters.itemTypeIds?.length &&
        item.itemTypeId &&
        !filters.itemTypeIds.includes(item.itemTypeId)
      ) {
        return false;
      }

      if (
        filters.symbolIds?.length &&
        item.descriptiveSymbolId &&
        !filters.symbolIds.includes(item.descriptiveSymbolId)
      ) {
        return false;
      }

      if (filters.itemCharacteristics?.length) {
        for (const characteristic of filters.itemCharacteristics) {
          if (characteristic === ItemCharacteristicsFilter.HARDWARE_BOX && !item.isHardwareBox)
            return false;
          if (characteristic === ItemCharacteristicsFilter.HIGH_RISK_HIGH_VALUE && !item.isHighRisk)
            return false;
          if (characteristic === ItemCharacteristicsFilter.PRO_GEAR && !item.isProGear)
            return false;
          if (characteristic === ItemCharacteristicsFilter.ME && !item.isMeCustom) return false;
        }
      }

      return true;
    });
  }, []);

  const getMergedInventoryItems: ItemJob[] = useMemo(() => {
    if (isCreatingNewOrder) {
      const localItems = actualInventory || [];
      return applyLocalFilters(localItems, actualInventoryFilters);
    }

    const localItems = actualInventory.filter((item) => !item.id && item.uuid);
    const backendItems = (actualInventory.filter((item) => item.id) as ItemJob[]) || [];
    const filteredLocalItems = applyLocalFilters(localItems, actualInventoryFilters);

    const inventory = [...backendItems, ...filteredLocalItems];

    return inventory;
  }, [actualInventory, actualInventoryFilters, applyLocalFilters]);

  const getUniqueLocations = useMemo(() => {
    const dataToUse = getMergedInventoryItems;

    if (!dataToUse || dataToUse.length === 0) return [];

    const locations = new Set<string>();

    dataToUse.forEach((item) => {
      if (item.storageBoxes?.[0]?.warehouse?.name) {
        locations.add(item.storageBoxes[0].warehouse.name);
      }
      if (item.storageBoxes?.[0]?.number) {
        locations.add(item.storageBoxes[0].number);
      }
      if (item.storageBoxLoadedType === StorageBoxLoadedTypeEnum.LIFTVAN) {
        const filteredLiftvanStorageBox = item.storageBoxes?.filter(
          (sb) => sb.storageBoxType?.name === StorageBoxLoadedTypeEnum.LIFTVAN
        );
        if (filteredLiftvanStorageBox?.length) {
          const sameName = filteredLiftvanStorageBox.find(
            (sb) => sb.number === item.storageBoxLoadedValue
          );
          if (sameName) locations.add(sameName.number);
        }
      }
    });

    return Array.from(locations).sort();
  }, [getMergedInventoryItems]);

  const handleActualInventoryFilter = useCallback((filters: any) => {
    setActualInventoryFilters(filters);
  }, []);

  useQuery({
    queryKey: DestinationKeys.getAll(["destination.list"]),
    queryFn: async () => {
      const response = await DestinationService.getAll({
        companyId: currentUser?.companyId,
        status: Status.ACTIVE,
        sortBy: JSON.stringify([["name"]]),
      });

      if (response && response.data && response.data instanceof Array) {
        return response.data;
      }

      return [] as Destination[];
    },
    onSuccess: (response) => {
      if (response instanceof Array) setCustomDestinations(response);
    },
    enabled: !!currentUser?.companyId && !externalList?.destinationList,
    refetchOnWindowFocus: false,
  });

  const effectiveLoading = useMemo(() => {
    if (isCreatingNewOrder) return false;

    return isActualInventoryLoading;
  }, [isCreatingNewOrder, isActualInventoryLoading]);

  useQuery({
    queryKey: ModeJobKeys.getAll(["mode.list"]),
    queryFn: async () => {
      const response = await ModeJobService.getAll({
        companyId: currentUser?.companyId,
        status: Status.ACTIVE,
        sortBy: JSON.stringify([["name"]]),
      });

      if (response && response.data && response.data instanceof Array) {
        return response.data;
      }

      return [] as JobMode[];
    },
    onSuccess: (response) => {
      if (response instanceof Array) setModes(response);
    },
    enabled: !!currentUser?.companyId && !externalList?.jobModeList,
    refetchOnWindowFocus: false,
  });

  const [storageBoxTypes, setStorageBoxTypes] = useState<StorageBoxType[]>([]);
  const [jobTypes, setJobTypes] = useState<JobType[]>([]);

  const getStorageBoxTypes = () => {
    return useQuery(
      ["inventory.storage.box.types.list"],
      () =>
        StorageBoxTypeService.getAll({
          companyId: currentUser?.companyId,
          sortBy: JSON.stringify([["updatedAt", "asc"]]),
          include: JSON.stringify([
            { association: "storageBoxes" },
            { association: "storageBoxTypeSealRules" },
          ]),
        }),
      {
        enabled: !!currentUser?.companyId,
        refetchOnWindowFocus: false,
        onSuccess: (response) => {
          if (response && response.data) setStorageBoxTypes(response.data);
        },
      }
    );
  };

  const getJobtypes = () => {
    return useQuery(
      ["inventory.storage.box.jobType.list"],
      () =>
        JobTypeService.getAll({
          companyId: currentUser?.companyId,
          status: Status.ACTIVE,
          sortBy: JSON.stringify([["name", "asc"]]),
        }),
      {
        enabled: !!currentUser?.companyId,
        refetchOnWindowFocus: false,
        onSuccess: (response) => {
          if (response && response.data && response.data?.length > 0) {
            setJobTypes(response.data);
          }
        },
      }
    );
  };

  const { isFetching: isLoadingStorageBoxTypes } = getStorageBoxTypes();
  const { isFetching: isLoadingJobTypes } = getJobtypes();

  const isCreate = quoteJobId < 0;

  const storageBoxModalRef = useRef<StorageBoxModalHandle>(null);

  const { mutate, isLoading } = useMutation(
    ["submit.storage.box"],
    (data: StorageBoxAssociateJob) => WarehousingService.setJobAndStorageRelation(data)
  );

  const renderInventory = useCallback(() => {
    if (typeOfInventory === InventoryType.ACTUAL) {
      return (
        <ActualInventory
          inventoryPdf={order?.jobInventoryPDF}
          itemsJobs={getMergedInventoryItems}
          isOnViewMode={isOnViewMode}
          orderStatus={orderStatus}
          storageBoxes={storageBoxes}
          uniqueLocations={getUniqueLocations}
          onFiltersChange={handleActualInventoryFilter}
          update={handleActualInventoryUpdate}
          updatePdfFile={(inventoryPdf: JobInventoryPDF | null) => {
            update?.("jobInventoryPDF", inventoryPdf)
          }}
          loadingData={effectiveLoading}
        />
      );
    }

    return (
      <QuotedInventory
        inventory={inventory}
        defaultMode={defaultMode}
        locations={destinations}
        jobId={quoteJobId}
        viewOnly={quotedViewOnly}
        isOnViewMode={isOnViewMode}
        externalList={externalList}
        update={(value: RoomJob[]) => update && update("inventory", value)}
        noHideOnExpand={!isCreate}
      />
    );
  }, [
    getMergedInventoryItems,
    typeOfInventory,
    isOnViewMode,
    orderStatus,
    storageBoxes,
    getUniqueLocations,
    handleActualInventoryFilter,
    effectiveLoading,
    inventory,
    defaultMode,
    destinations,
    quoteJobId,
    quotedViewOnly,
    externalList,
    isCreate,
    update,
    handleActualInventoryUpdate,
  ]);

  const addressDestinationItems = useMemo(() => {
    const destinationsId = destinations?.map((address) => address.id);
    const destinationItems: { [x: string]: QuotedItem[] } = {};

    destinationsId?.forEach((destinationId) => {
      if (destinationId) {
        const items: QuotedItem[] = [];

        inventory?.forEach((inventoryValue) => {
          inventoryValue?.itemsJob?.forEach((itemJob) => {
            if (
              itemJob &&
              itemJob?.itemJob &&
              itemJob?.itemJob?.destinationResidenceId &&
              itemJob?.itemJob?.destinationResidenceId === destinationId
            ) {
              items.push(itemJob);
            }
          });

          inventoryValue?.materials?.forEach((material) => {
            if (
              material &&
              material?.serviceBooking?.isItem &&
              material?.destinationResidenceId &&
              material?.destinationResidenceId === destinationId
            ) {
              const itemJob = {
                itemJob: material,
              } as QuotedItem;

              items.push(itemJob);
            }
          });
        });
        if (items && items?.length > 0) {
          destinationItems[`id-${destinationId.toString()}`] = items;
        }
      }
    });

    return destinationItems;
  }, [destinations, inventory]);

  const customDestinationItems = useMemo(() => {
    const destinationsId = customDestinations
      ?.filter((destination) => !destination.name?.includes(DESTINATION.NOT_GOING))
      .map((destination) => destination.id);

    const destinationItems: { [x: string]: QuotedItem[] } = {};

    destinationsId?.forEach((destinationId) => {
      if (destinationId) {
        const items: QuotedItem[] = [];

        inventory?.forEach((inventoryValue) => {
          inventoryValue?.itemsJob?.forEach((itemJob) => {
            if (itemJob && itemJob?.itemJob && itemJob?.itemJob?.destinationId === destinationId) {
              items.push(itemJob);
            }
          });
          inventoryValue?.materials?.forEach((material) => {
            if (
              material &&
              material?.serviceBooking?.isItem &&
              material?.destinationId &&
              material?.destinationId === destinationId
            ) {
              const itemJob = {
                itemJob: material,
              } as QuotedItem;

              items.push(itemJob);
            }
          });
        });

        if (items && items?.length > 0) {
          destinationItems[`id-${destinationId.toString()}`] = items;
        }
      }
    });

    return destinationItems;
  }, [customDestinations, inventory]);

  const customModeDestinationItems = useMemo(() => {
    const modeDestinationsId = modes
      ?.filter((modeDestination) => modeDestination.isDestination)
      .map((modeDestination) => modeDestination.id);

    const modeDestinationItems: { [x: string]: QuotedItem[] } = {};

    modeDestinationsId?.forEach((modeDestinationId) => {
      if (modeDestinationId) {
        const items: QuotedItem[] = [];

        inventory?.forEach((inventoryValue) => {
          inventoryValue?.itemsJob?.forEach((itemJob) => {
            if (
              itemJob &&
              itemJob?.itemJob &&
              itemJob?.itemJob?.modeDestinationId === modeDestinationId
            ) {
              items.push(itemJob);
            }
          });
          inventoryValue?.materials?.forEach((material) => {
            if (
              material &&
              material?.serviceBooking?.isItem &&
              material?.modeDestinationId === modeDestinationId
            ) {
              const itemJob = {
                itemJob: material,
              } as QuotedItem;

              items.push(itemJob);
            }
          });
        });

        if (items && items?.length > 0) {
          modeDestinationItems[`id-${modeDestinationId.toString()}`] = items;
        }
      }
    });

    return modeDestinationItems;
  }, [modes, inventory]);

  const quotedInventoryItems = useMemo(() => {
    const quotedItems: QuotedItem[] = [];

    Object.keys(addressDestinationItems).forEach((addressId) => {
      const address = destinations?.find(
        (addressItem) => String(addressItem.id) === String(addressId?.replace("id-", ""))
      );
      const addressItems = addressDestinationItems[addressId];

      if (!address || !addressItems) return null;

      quotedItems.push(...addressItems);
    });

    Object.keys(customDestinationItems).forEach((destinationId) => {
      const destination = customDestinations?.find(
        (destination) => String(destination.id) === String(destinationId?.replace("id-", ""))
      );

      const customItems = customDestinationItems[destinationId];

      if (!destination || !customItems) return null;

      quotedItems.push(...customItems);
    });

    Object.keys(customModeDestinationItems).forEach((modeDestinationId) => {
      const modeDestination = modes?.find(
        (modeDestination) =>
          String(modeDestination.id) === String(modeDestinationId?.replace("id-", ""))
      );
      const modeItems = customModeDestinationItems[modeDestinationId];

      if (!modeDestination || !modeItems) return null;

      quotedItems.push(...modeItems);
    });

    return quotedItems?.map((inventoryItems) => inventoryItems?.itemJob || []) || [];
  }, [
    addressDestinationItems,
    customDestinationItems,
    customModeDestinationItems,
    destinations,
    customDestinations,
    modes,
  ]);

  const storageBoxesForCards = useMemo(() => {
    const result: StorageBox[] = [];

    if (Array.isArray(storageBoxes)) {
      result.push(...storageBoxes);
    }

    const dataToUse =
      typeOfInventory === InventoryType.ACTUAL ? actualInventory : quotedInventoryItems;

    if (Array.isArray(dataToUse)) {
      dataToUse.forEach((item) => {
        if (
          item.storageBoxLoadedType === StorageBoxLoadedTypeEnum.LIFTVAN &&
          item.storageBoxLoadedValue
        ) {
          const alreadyExists = result.some((box) => box.number === item.storageBoxLoadedValue);

          if (!alreadyExists) {
            const filteredLiftvanStorageBox = item.storageBoxes?.filter(
              (sb) => sb.storageBoxType?.name === StorageBoxLoadedTypeEnum.LIFTVAN
            );
            if (filteredLiftvanStorageBox?.length) {
              const sameName = filteredLiftvanStorageBox.find(
                (sb) => sb.number === item.storageBoxLoadedValue
              );
              if (sameName)
                result.push({
                  number: sameName.number,
                } as StorageBox);
            }
          }
        }
      });
    }

    return result.sort((a, b) => a.number.localeCompare(b.number));
  }, [storageBoxes, actualInventory, quotedInventoryItems, typeOfInventory]);

  const renderInfoVolume = useMemo(() => {
    if (typeOfInventory === InventoryType.ACTUAL) {
      return (
        <Cards
          items={actualInventory}
          storageBoxes={storageBoxesForCards}
          isOnViewMode={isOnViewMode}
          onAddStorageBox={
            order?.id
              ? (view?: boolean) =>
                  storageBoxModalRef?.current?.open(
                    storageBoxes.map((storageBox) => storageBox.id!),
                    view
                  )
              : undefined
          }
        />
      );
    }

    return (
      <Cards
        items={quotedInventoryItems}
        volume={order?.quotedVolume}
        weight={order?.quotedWeight}
        storageBoxes={storageBoxes?.sort((a, b) => a.number.localeCompare(b.number))}
        isOnViewMode={isOnViewMode}
        onAddStorageBox={
          order?.id
            ? (view?: boolean) =>
                storageBoxModalRef?.current?.open(
                  storageBoxes.map((storageBox) => storageBox.id!),
                  view
                )
            : undefined
        }
      />
    );
  }, [order, storageBoxes, typeOfInventory]);

  const submitStorageBox = (ids: number[], callback?: Function) => {
    if (ids)
      mutate(
        {
          job: { id: quoteJobId } as Job,
          storageBoxes: ids.map(
            (id) =>
              ({
                id,
              } as StorageBox)
          ),
        },
        {
          onSuccess: (response) => {
            if (response?.data && response.data instanceof Array)
              handleUpdateOrder?.("storageBoxes", response?.data || []);

            NotificationToast.success(translate("operationPerformedSuccessfully"));
            storageBoxModalRef.current?.close(order);

            if (callbackFunction) callbackFunction();
            if (callback) callback?.();
          },
        }
      );
  };

  const updateActualInventory = (storageBoxRemoved: number) => {
    if (storageBoxRemoved) {
      const actualInventoryClone = _.cloneDeep(actualInventory);

      actualInventoryClone.forEach((inventory) => {
        if (inventory.storageBoxes?.length) {
          const storageBoxFound = inventory.storageBoxes?.find(
            (storageBox) => storageBox.id === storageBoxRemoved
          );

          if (storageBoxFound) {
            const indexOfSb = inventory.storageBoxes?.indexOf(storageBoxFound);

            if (indexOfSb !== undefined && indexOfSb !== -1)
              inventory.storageBoxes?.splice(indexOfSb, 1);
          }
        }
      });

      update?.("actualInventory", actualInventoryClone);
    }
  };

  return (
    <div>
      <div
        className={
          compact
            ? classes.compactContainer
            : isOnViewMode
            ? classes.container
            : classes.maintenanceContainer
        }
      >
        <Header
          rightSide={renderInfoVolume}
          inventoryType={typeOfInventory}
          setInventoryType={setTypeOfInventory}
        />

        {renderInventory()}

        {!isOnViewMode && showActionButtons ? (
          <div className={classes.buttons}>
            <ActionButtons
              cancelDisabled={isUpdating}
              saveDisabled={isUpdating}
              onCancel={onCancel}
              onSavePublish={!isUpdating ? onSavePublish : undefined}
              onSave={onSave}
            />
          </div>
        ) : null}

        <StorageBoxModal
          ref={storageBoxModalRef}
          job={order}
          onSubmit={submitStorageBox}
          isLoading={isLoading}
          view={isOnViewMode}
          updateJob={(attribute, order) => update?.(attribute, order)}
          updateActualInventory={updateActualInventory}
          addStorageBox={addStorageBox}
          removeStorageBox={removeStorageBox}
          jobTypes={jobTypes}
          storageBoxTypes={storageBoxTypes}
          isLoadingData={isLoadingStorageBoxTypes ?? isLoadingJobTypes}
        />

        <LoadingModal showing={isUpdating} />
      </div>
    </div>
  );
};

export default OrderInventory;
